/**
 * Pure stock classification — mirrors isalbi-periscope `app/lib/stockTypeClassification.ts`.
 * Test vectors: `tests/fixtures/stockTypeClassificationCases.json` (sync with Periscope
 * `app/lib/__fixtures__/stockTypeClassificationCases.json`).
 *
 * See isalbi-periscope docs/stock-type-classification.md and README-stock-type.md (this folder).
 */

/**
 * @param {string|null|undefined} raw
 * @returns {string|null}
 */
function normalizeSohStatusForComparison(raw) {
  if (raw == null) return null;
  const t = String(raw)
    .replace(/\u00A0/g, ' ')
    .replace(/[\u2000-\u200A\u202F\u205F\u3000]/g, ' ')
    .trim();
  if (t === '') return null;
  return t.toLowerCase();
}

/**
 * Collapse spaces/hyphens/underscores (mirrors isalbi-periscope stockTypeClassification.ts).
 * @param {string} normalizedLower
 * @returns {string}
 */
function sohStatusCompact(normalizedLower) {
  return normalizedLower
    .normalize('NFKC')
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/[\s_-]+/g, '');
}

/**
 * @param {{ sohStatusRaw?: string|null|undefined, sohStatuses?: string[]|null|undefined, eta: string|null|undefined, inventoryQuantity: number }} input
 * @returns {'available'|'backorder'}
 */
function classifyBaseStockType(input) {
  const { sohStatusRaw, sohStatuses, eta, inventoryQuantity } = input;
  var norms = [];
  if (sohStatuses != null && sohStatuses.length > 0) {
    for (var i = 0; i < sohStatuses.length; i++) {
      var n = normalizeSohStatusForComparison(sohStatuses[i]);
      if (n) norms.push(n);
    }
  } else {
    var single = normalizeSohStatusForComparison(sohStatusRaw);
    if (single) norms.push(single);
  }
  if (norms.length > 0) {
    for (var j = 0; j < norms.length; j++) {
      if (sohStatusCompact(norms[j]) === 'preorder') {
        return 'backorder';
      }
    }
    for (var k = 0; k < norms.length; k++) {
      if (norms[k] === 'in stock' || sohStatusCompact(norms[k]) === 'instock') {
        return 'available';
      }
    }
  }
  const etaClean = eta == null || String(eta).trim() === '' ? null : String(eta).trim();
  if (etaClean && inventoryQuantity < 2) {
    return 'backorder';
  }
  return 'available';
}

function pushSohFromNodes(sohList, nodes) {
  if (!nodes || !Array.isArray(nodes)) return;
  for (var i = 0; i < nodes.length; i++) {
    var m = nodes[i];
    if (m.namespace === 'custom' && m.key === 'soh_status' && m.value) {
      var v = String(m.value).trim();
      if (v) sohList.push(v);
    }
  }
}

/**
 * Storefront / product JSON variant shape (same sources as product-form calculateStockTypeProperties).
 * @param {Object} variantData
 * @returns {{ sohStatuses: string[], sohStatusRaw: string|null, eta: string|null, inventoryQuantity: number }}
 */
function extractClassificationInputsFromStorefrontVariant(variantData) {
  const inventoryQuantity = variantData.inventory_quantity || 0;
  const metafields = variantData.metafields || {};

  var productSoh = [];
  var variantSoh = [];

  if (variantData.product && variantData.product.metafields) {
    const pm = variantData.product.metafields;
    if (pm.custom && pm.custom.soh_status) {
      const v = pm.custom.soh_status;
      var pv = typeof v === 'object' && v !== null && 'value' in v ? v.value : v;
      if (pv) productSoh.push(String(pv).trim());
    } else if (pm.custom && typeof pm.custom === 'object') {
      const direct = pm.custom.soh_status;
      if (direct) {
        var pdv = typeof direct === 'object' ? direct.value : direct;
        if (pdv) productSoh.push(String(pdv).trim());
      }
    }
  }

  if (metafields.custom && metafields.custom.soh_status) {
    const v = metafields.custom.soh_status;
    var sv = typeof v === 'object' && v !== null && 'value' in v ? v.value : v;
    if (sv) variantSoh.push(String(sv).trim());
  } else if (metafields.custom && typeof metafields.custom === 'object') {
    const direct = metafields.custom.soh_status;
    if (direct) {
      var dv = typeof direct === 'object' ? direct.value : direct;
      if (dv) variantSoh.push(String(dv).trim());
    }
  }

  if (variantData.product && variantData.product.metafields && Array.isArray(variantData.product.metafields)) {
    pushSohFromNodes(productSoh, variantData.product.metafields);
  }

  if (Array.isArray(metafields)) {
    pushSohFromNodes(variantSoh, metafields);
  }

  function clean(list) {
    return list.filter(function (s) {
      return s && String(s).trim() !== '';
    });
  }
  productSoh = clean(productSoh);
  variantSoh = clean(variantSoh);

  var sohList = productSoh.length > 0 ? productSoh : variantSoh;

  let eta = null;
  if (metafields.inventory && metafields.inventory.eta) {
    const v = metafields.inventory.eta;
    eta = typeof v === 'object' && v !== null && 'value' in v ? v.value : v;
  } else if (metafields.custom && metafields.custom.eta) {
    const v = metafields.custom.eta;
    eta = typeof v === 'object' && v !== null && 'value' in v ? v.value : v;
  }

  if (!eta && variantData.product && variantData.product.metafields) {
    const pm = variantData.product.metafields;
    if (pm.inventory && pm.inventory.eta) {
      const v = pm.inventory.eta;
      eta = typeof v === 'object' && v !== null && 'value' in v ? v.value : v;
    } else if (pm.custom && pm.custom.eta) {
      const v = pm.custom.eta;
      eta = typeof v === 'object' && v !== null && 'value' in v ? v.value : v;
    }
  }

  if (!eta && Array.isArray(metafields)) {
    const etaMetafield = metafields.find(function (m) {
      return (m.namespace === 'inventory' || m.namespace === 'custom') && m.key === 'eta';
    });
    if (etaMetafield) {
      eta = etaMetafield.value != null ? etaMetafield.value : etaMetafield;
    }
  }

  if (!eta && metafields.eta) {
    const v = metafields.eta;
    eta = typeof v === 'object' && v !== null && 'value' in v ? v.value : v;
  }

  if (!eta && metafields.value) {
    eta = metafields.value;
  }

  if (eta === '' || eta === null || eta === undefined) {
    eta = null;
  }

  return {
    sohStatuses: sohList,
    sohStatusRaw: sohList.length > 0 ? sohList[0] : null,
    eta: eta,
    inventoryQuantity: inventoryQuantity
  };
}

/**
 * Minimal cart row shape: { metafields: { inventory: { eta } }, inventory_quantity, optional custom soh }.
 * Optional `data-variant-soh-status` on DOM is passed as variantData._datasetSohStatus for cart lines.
 * @param {Object} variantData
 * @returns {'available'|'backorder'}
 */
function calculateBaseStockTypeFromVariant(variantData) {
  const inputs = extractClassificationInputsFromStorefrontVariant(variantData);
  if (
    variantData._datasetSohStatus != null &&
    String(variantData._datasetSohStatus).trim() !== '' &&
    (!inputs.sohStatuses || inputs.sohStatuses.length === 0)
  ) {
    var ds = String(variantData._datasetSohStatus).trim();
    inputs.sohStatuses = [ds];
    inputs.sohStatusRaw = ds;
  }
  return classifyBaseStockType(inputs);
}

var __isalbiStockTypeClassificationExports = {
  normalizeSohStatusForComparison: normalizeSohStatusForComparison,
  sohStatusCompact: sohStatusCompact,
  classifyBaseStockType: classifyBaseStockType,
  extractClassificationInputsFromStorefrontVariant: extractClassificationInputsFromStorefrontVariant,
  calculateBaseStockTypeFromVariant: calculateBaseStockTypeFromVariant
};

if (typeof module !== 'undefined' && module.exports) {
  module.exports = __isalbiStockTypeClassificationExports;
}
if (typeof window !== 'undefined') {
  window.__isalbiStockTypeClassification = __isalbiStockTypeClassificationExports;
}
